import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@reflex/ui/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@reflex/ui/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@reflex/ui/components/ui/command';
import { SectionLabel, PageDescription } from '@reflex/ui/components/layout/Typography';
import { DescriptionHint } from '@reflex/ui/components/shared/DescriptionHint';
import { Label } from '@reflex/ui/components/ui/label';
import { cn } from '@reflex/ui/lib/utils';
import {
  filterModelsByProvider,
  resolveEffectiveAdminProvider,
  resolveDisplayedModel,
  toDropdownValue,
  DROPDOWN_NONE_SENTINEL,
} from '@/lib/agent-model-ui';
import type { AgentModelProvider, DiscoveredModel } from '@reflex/shared';

const NONE_SENTINEL = DROPDOWN_NONE_SENTINEL;
const ADMIN_DEFAULTS_DESCRIPTION =
  'Override the system-recommended defaults for the Launch dialog.';

export interface AdminDefaultsSectionProps {
  enabledProviders: AgentModelProvider[];
  discoveredModels: DiscoveredModel[];
  savedProviderId: string | undefined;
  currentDefaultProvider: string | undefined;
  currentDefaultModel: string | undefined;
  isProviderEnabled: (providerId: string) => boolean;
  isPending: boolean;
  onSetDefault: (next: { providerId?: string; modelId?: string }) => void;
  readOnly?: boolean;
}

export function AdminDefaultsSection({
  enabledProviders,
  discoveredModels,
  savedProviderId,
  currentDefaultProvider,
  currentDefaultModel,
  isProviderEnabled,
  isPending,
  onSetDefault,
  readOnly = false,
}: AdminDefaultsSectionProps) {
  // Local UI state for the provider dropdown. The backend only stores complete
  // (providerId, modelId) pairs, so we track the user's in-progress provider
  // choice locally to filter the model dropdown without writing a partial
  // default. Initialized from the saved default; remains the user's selection
  // until they pick a model (which persists) or pick None (which clears).
  //
  // Track the previous saved value to reset local state when the prop changes
  // (e.g. after refetch). This pattern is recommended by React over useEffect.
  const [prevSavedProviderId, setPrevSavedProviderId] = useState(savedProviderId);
  const [selectedProviderForFilter, setSelectedProviderForFilter] = useState<string | undefined>(
    savedProviderId,
  );
  const [modelPopoverOpen, setModelPopoverOpen] = useState(false);

  if (savedProviderId !== prevSavedProviderId) {
    setPrevSavedProviderId(savedProviderId);
    setSelectedProviderForFilter(savedProviderId);
  }

  // Single-provider detection: when there's only one ENABLED provider, auto-select it
  const isSingleEnabledProvider = enabledProviders.length === 1;
  const singleEnabledProviderId = isSingleEnabledProvider ? enabledProviders[0]?.id : undefined;

  // The provider shown in the dropdown cascades through available options
  const effectiveSelectedProvider = resolveEffectiveAdminProvider({
    isSingleEnabledProvider,
    singleEnabledProviderId,
    selectedProviderForFilter,
    currentDefaultProvider,
    isProviderEnabled,
  });

  const displayedProvider = toDropdownValue(effectiveSelectedProvider, NONE_SENTINEL);

  // Filter the model dropdown by the effective provider
  const filteredModelsForDropdown = filterModelsByProvider(
    discoveredModels,
    effectiveSelectedProvider,
  );

  // The displayed model only shows the saved value when it belongs to the
  // currently selected provider
  const displayedModel = resolveDisplayedModel(
    currentDefaultModel,
    effectiveSelectedProvider,
    discoveredModels,
  );

  const handleDefaultProviderChange = (value: string) => {
    // Single-enabled-provider agents have the dropdown disabled, but guard anyway
    if (readOnly || isSingleEnabledProvider) return;

    if (value === NONE_SENTINEL) {
      // Clearing the provider clears any saved default and the local filter
      setSelectedProviderForFilter(undefined);
      if (currentDefaultProvider || currentDefaultModel) {
        onSetDefault({ providerId: undefined, modelId: undefined });
      }
      return;
    }

    // Picking a provider only updates local state (filters the model list).
    // The saved default isn't written until a model is picked. If a saved
    // default exists for a different provider, clear it so we don't display
    // a stale model in the dropdown.
    setSelectedProviderForFilter(value);
    if (currentDefaultProvider && currentDefaultProvider !== value) {
      onSetDefault({ providerId: undefined, modelId: undefined });
    }
  };

  const handleDefaultModelChange = (value: string) => {
    if (readOnly) return;
    if (value === NONE_SENTINEL) {
      // Clearing the model clears the saved default. Keep the local provider
      // filter so the user can pick a different model under the same provider.
      if (currentDefaultProvider || currentDefaultModel) {
        onSetDefault({ providerId: undefined, modelId: undefined });
      }
      setModelPopoverOpen(false);
      return;
    }

    const selectedModel = discoveredModels.find((m) => m.id === value);
    if (!selectedModel) {
      // Model not in the discovered list — shouldn't happen; clear to be safe.
      onSetDefault({ providerId: undefined, modelId: undefined });
      setModelPopoverOpen(false);
      return;
    }

    // Persist provider + model together. Use the model's actual provider
    // (which always matches the local filter at this point because the
    // dropdown only shows models from the filtered provider).
    const providerId = isSingleEnabledProvider ? singleEnabledProviderId : selectedModel.providerId;
    setSelectedProviderForFilter(providerId);
    onSetDefault({ providerId, modelId: value });
    setModelPopoverOpen(false);
  };

  return (
    <div className="border-t pt-4">
      <div className="mb-3 flex items-center gap-1">
        <SectionLabel className="mb-0">Configure Agent Default</SectionLabel>
        <DescriptionHint className="sm:hidden" ariaLabel="About this section">
          {ADMIN_DEFAULTS_DESCRIPTION}
        </DescriptionHint>
      </div>
      <PageDescription className="mb-3 max-sm:hidden text-xs">
        {ADMIN_DEFAULTS_DESCRIPTION}
      </PageDescription>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="min-w-0">
          <Label className="text-xs text-muted-foreground mb-1.5 block">Default Provider</Label>
          <Select
            value={displayedProvider}
            onValueChange={handleDefaultProviderChange}
            disabled={readOnly || isPending || isSingleEnabledProvider}
          >
            <SelectTrigger className="text-xs h-8 w-full">
              <SelectValue placeholder="None (system decides)" />
            </SelectTrigger>
            <SelectContent className="**:data-[slot=select-item]:text-xs">
              {!isSingleEnabledProvider && (
                <SelectItem value={NONE_SENTINEL}>None (system decides)</SelectItem>
              )}
              {enabledProviders.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-0">
          <Label className="text-xs text-muted-foreground mb-1.5 block">Default Model</Label>
          <Popover open={modelPopoverOpen} onOpenChange={setModelPopoverOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                role="combobox"
                aria-expanded={modelPopoverOpen}
                aria-label="Select default model"
                disabled={readOnly || isPending}
                className={cn(
                  'flex h-8 w-full items-center justify-between gap-1 rounded-md border border-input bg-transparent px-2.5 text-xs shadow-xs transition-[color,box-shadow] outline-none',
                  'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
                  'dark:bg-input/30 dark:hover:bg-input/50',
                  (readOnly || isPending) && 'opacity-50 cursor-not-allowed',
                )}
              >
                <span className="truncate">
                  {displayedModel
                    ? (filteredModelsForDropdown.find((m) => m.id === displayedModel)
                        ?.displayName ?? displayedModel)
                    : 'None (system decides)'}
                </span>
                <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search models…" className="h-8 text-xs" />
                <CommandList>
                  <CommandEmpty className="py-3 text-xs">No models found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="__none_system_decides__"
                      onSelect={() => handleDefaultModelChange(NONE_SENTINEL)}
                      className="text-xs"
                    >
                      <Check
                        className={cn(
                          'mr-1.5 h-3 w-3 shrink-0',
                          !displayedModel ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      None (system decides)
                    </CommandItem>
                    {filteredModelsForDropdown.map((m) => (
                      <CommandItem
                        key={m.id}
                        value={m.displayName}
                        onSelect={() => handleDefaultModelChange(m.id)}
                        className="text-xs"
                      >
                        <Check
                          className={cn(
                            'mr-1.5 h-3 w-3 shrink-0',
                            displayedModel === m.id ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                        {m.displayName}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );
}
